/**
 * Who is signed in, kept across a reload — and let go when the till sits idle.
 *
 * Specification: ../test/held-sign-in.spec.ts.
 *
 * A tablet reloads: a service-worker update, a browser that tidied a tab, a
 * cable pulled. Signing in again each time was friction a server met several
 * times a shift. So the sign-in is held in the device's storage with the time
 * of the last touch, and restored only while that is recent. A till left alone
 * for longer locks itself, so the next person to pick it up signs in as
 * themselves rather than as whoever walked away.
 *
 * Storage can throw (private mode, quota), so every read and write is guarded:
 * a till that cannot remember simply asks again.
 */

export interface HeldSignIn {
  readonly staffId: string;
  readonly displayName: string;
  readonly roleId: string | null;
  readonly lastActiveAt: number;
}

const KEY = "gf.pos.signedIn";

/** How long a till may sit untouched before it locks. */
export const IDLE_LOCK_MS = 10 * 60_000;

export function holdSignIn(storage: Storage | null, held: HeldSignIn): void {
  try {
    storage?.setItem(KEY, JSON.stringify(held));
  } catch {
    // Nothing to do: the next reload asks for a PIN, which is safe.
  }
}

export function releaseSignIn(storage: Storage | null): void {
  try {
    storage?.removeItem(KEY);
  } catch {
    // As above.
  }
}

/** The held sign-in, if it was touched within `idleMs` of `now`; otherwise it is released. */
export function restoreSignIn(storage: Storage | null, now: number, idleMs = IDLE_LOCK_MS): HeldSignIn | null {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const held = JSON.parse(raw) as Partial<HeldSignIn>;
    if (
      typeof held.staffId !== "string" ||
      typeof held.displayName !== "string" ||
      typeof held.lastActiveAt !== "number" ||
      now - held.lastActiveAt > idleMs ||
      held.lastActiveAt > now + 60_000
    ) {
      releaseSignIn(storage);
      return null;
    }
    return {
      staffId: held.staffId,
      displayName: held.displayName,
      roleId: typeof held.roleId === "string" ? held.roleId : null,
      lastActiveAt: held.lastActiveAt,
    };
  } catch {
    releaseSignIn(storage);
    return null;
  }
}

/** Record a touch. Cheap to call on every tap; writes at most every `everyMs`. */
export function touchSignIn(storage: Storage | null, now: number, everyMs = 15_000): void {
  const held = restoreSignIn(storage, now);
  if (held && now - held.lastActiveAt >= everyMs) holdSignIn(storage, { ...held, lastActiveAt: now });
}

/** Whether a till last touched at `lastActiveAt` should lock now. */
export const shouldLock = (lastActiveAt: number, now: number, idleMs = IDLE_LOCK_MS): boolean =>
  now - lastActiveAt > idleMs;
